import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { FileText, Send, CheckCircle, Star, MessageSquare } from "lucide-react";
import {
  useModuleSubmissions,
  useMySubmission,
  useSubmitHomework,
  useUpdateHomework,
  useAddInstructorFeedback,
  useSubmissionPeerReviews,
  useCreatePeerReview,
  HomeworkSubmission,
} from "@/hooks/useHomework";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface HomeworkSubmissionSectionProps {
  moduleId: string;
  courseInstructorId?: string;
}

export function HomeworkSubmissionSection({
  moduleId,
  courseInstructorId,
}: HomeworkSubmissionSectionProps) {
  const { user } = useAuth();
  const isInstructor = useHasRole("instructor");
  const isAdmin = useHasRole("admin");
  const { data: mySubmission, isLoading: loadingMy } = useMySubmission(
    moduleId,
    user?.id || ""
  );
  const { data: allSubmissions, isLoading: loadingAll } =
    useModuleSubmissions(moduleId);

  const canViewAllSubmissions =
    isInstructor || isAdmin || user?.id === courseInstructorId;

  if (loadingMy) {
    return <Skeleton className="h-48 w-full" />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Homework Submission
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mySubmission ? (
            <MySubmissionView
              submission={mySubmission}
              moduleId={moduleId}
              userId={user?.id || ""}
            />
          ) : (
            <SubmitHomeworkForm moduleId={moduleId} userId={user?.id || ""} />
          )}
        </CardContent>
      </Card>

      {canViewAllSubmissions && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAll ? (
              <Skeleton className="h-32 w-full" />
            ) : allSubmissions && allSubmissions.length > 0 ? (
              <div className="space-y-4">
                {allSubmissions.map((submission) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    canProvideFeedback={
                      isInstructor ||
                      isAdmin ||
                      user?.id === courseInstructorId
                    }
                    instructorId={user?.id || ""}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No submissions yet.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {mySubmission && allSubmissions && allSubmissions.length > 1 && (
        <PeerReviewSection
          submissions={allSubmissions.filter((s) => s.user_id !== user?.id)}
          currentUserId={user?.id || ""}
        />
      )}
    </div>
  );
}

function SubmitHomeworkForm({
  moduleId,
  userId,
}: {
  moduleId: string;
  userId: string;
}) {
  const [content, setContent] = useState("");
  const submitHomework = useSubmitHomework();

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error("Please enter your homework content");
      return;
    }

    try {
      await submitHomework.mutateAsync({
        moduleId,
        userId,
        content,
      });
      toast.success("Homework submitted successfully");
      setContent("");
    } catch {
      toast.error("Failed to submit homework");
    }
  };

  return (
    <div className="space-y-4">
      <Textarea
        placeholder="Enter your homework submission..."
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={6}
      />
      <Button
        onClick={handleSubmit}
        disabled={submitHomework.isPending || !content.trim()}
      >
        <Send className="h-4 w-4 mr-2" />
        Submit Homework
      </Button>
    </div>
  );
}

function MySubmissionView({
  submission,
  moduleId,
  userId,
}: {
  submission: HomeworkSubmission;
  moduleId: string;
  userId: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(submission.content || "");
  const updateHomework = useUpdateHomework();

  const handleUpdate = async () => {
    try {
      await updateHomework.mutateAsync({
        submissionId: submission.id,
        content,
      });
      toast.success("Homework updated");
      setIsEditing(false);
    } catch {
      toast.error("Failed to update homework");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Submitted{" "}
          {formatDistanceToNow(new Date(submission.submitted_at), {
            addSuffix: true,
          })}
        </Badge>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={6}
          />
          <div className="flex gap-2">
            <Button onClick={handleUpdate} disabled={updateHomework.isPending}>
              Save Changes
            </Button>
            <Button variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-muted rounded-lg">
          <p className="whitespace-pre-wrap">{submission.content}</p>
        </div>
      )}

      {submission.instructor_feedback && (
        <div className="mt-4">
          <Separator className="my-4" />
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary" />
            Instructor Feedback
          </h4>
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="whitespace-pre-wrap">
              {submission.instructor_feedback}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmissionCard({
  submission,
  canProvideFeedback,
  instructorId,
}: {
  submission: HomeworkSubmission;
  canProvideFeedback: boolean;
  instructorId: string;
}) {
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedback, setFeedback] = useState(submission.instructor_feedback || "");
  const addFeedback = useAddInstructorFeedback();

  const handleSubmitFeedback = async () => {
    try {
      await addFeedback.mutateAsync({
        submissionId: submission.id,
        feedback,
        instructorId,
      });
      toast.success("Feedback added");
      setShowFeedbackForm(false);
    } catch {
      toast.error("Failed to add feedback");
    }
  };

  return (
    <div className="p-4 border rounded-lg">
      <div className="flex items-start gap-3">
        <UserAvatar avatarPath={submission.author?.avatar_url} fullName={submission.author?.full_name} className="h-8 w-8" thumbnailSize={64} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="font-medium">{submission.author?.full_name}</span>
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(submission.submitted_at), {
                addSuffix: true,
              })}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
            {submission.content}
          </p>

          {submission.instructor_feedback && (
            <div className="mt-3 p-3 bg-primary/5 rounded-lg">
              <p className="text-xs font-medium text-primary mb-1">
                Instructor Feedback:
              </p>
              <p className="text-sm">{submission.instructor_feedback}</p>
            </div>
          )}

          {canProvideFeedback && !showFeedbackForm && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => setShowFeedbackForm(true)}
            >
              {submission.instructor_feedback ? "Edit Feedback" : "Add Feedback"}
            </Button>
          )}

          {showFeedbackForm && (
            <div className="mt-3 space-y-2">
              <Textarea
                placeholder="Enter your feedback..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSubmitFeedback}
                  disabled={addFeedback.isPending}
                >
                  Submit Feedback
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFeedbackForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PeerReviewSection({
  submissions,
  currentUserId,
}: {
  submissions: HomeworkSubmission[];
  currentUserId: string;
}) {
  const [selectedSubmission, setSelectedSubmission] = useState<string | null>(
    null
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Star className="h-5 w-5" />
          Peer Review
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Review your peers' submissions to help them improve.
        </p>
        <div className="space-y-3">
          {submissions.map((submission) => (
            <div key={submission.id}>
              <div
                className="p-3 border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() =>
                  setSelectedSubmission(
                    selectedSubmission === submission.id ? null : submission.id
                  )
                }
              >
                <div className="flex items-center gap-2">
                  <UserAvatar avatarPath={submission.author?.avatar_url} fullName={submission.author?.full_name} className="h-6 w-6" thumbnailSize={64} />
                  <span className="text-sm font-medium">
                    {submission.author?.full_name}
                  </span>
                </div>
              </div>
              {selectedSubmission === submission.id && (
                <PeerReviewPanel
                  submission={submission}
                  reviewerId={currentUserId}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PeerReviewPanel({
  submission,
  reviewerId,
}: {
  submission: HomeworkSubmission;
  reviewerId: string;
}) {
  const { data: reviews, isLoading } = useSubmissionPeerReviews(submission.id);
  const createReview = useCreatePeerReview();
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState<number>(0);

  const hasReviewed = reviews?.some((r) => r.reviewer_id === reviewerId);

  const handleSubmitReview = async () => {
    if (!feedback.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    try {
      await createReview.mutateAsync({
        submissionId: submission.id,
        reviewerId,
        feedback,
        rating: rating || undefined,
      });
      toast.success("Review submitted");
      setFeedback("");
      setRating(0);
    } catch {
      toast.error("Failed to submit review");
    }
  };

  return (
    <div className="mt-2 p-4 bg-muted/50 rounded-lg space-y-4">
      <div>
        <h5 className="text-sm font-medium mb-2">Submission:</h5>
        <p className="text-sm whitespace-pre-wrap">{submission.content}</p>
      </div>

      <Separator />

      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <>
          {reviews && reviews.length > 0 && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium">Reviews:</h5>
              {reviews.map((review) => (
                <div key={review.id} className="p-3 bg-background rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <UserAvatar avatarPath={review.reviewer?.avatar_url} fullName={review.reviewer?.full_name} className="h-5 w-5" thumbnailSize={64} />
                    <span className="text-xs font-medium">
                      {review.reviewer?.full_name}
                    </span>
                    {review.rating && (
                      <div className="flex items-center gap-1 ml-auto">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < review.rating!
                                ? "fill-primary text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-sm">{review.feedback}</p>
                </div>
              ))}
            </div>
          )}

          {!hasReviewed && (
            <div className="space-y-3">
              <h5 className="text-sm font-medium">Add Your Review:</h5>
              <div className="flex items-center gap-2">
                <span className="text-sm">Rating:</span>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-5 w-5 ${
                        star <= rating
                          ? "fill-primary text-primary"
                          : "text-muted-foreground"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <Textarea
                placeholder="Enter your feedback for this submission..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
              />
              <Button
                size="sm"
                onClick={handleSubmitReview}
                disabled={createReview.isPending}
              >
                Submit Review
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
